import { zodResolver } from '@hookform/resolvers/zod'
import {
  type CreatePropertyInput,
  CreatePropertyInputSchema,
  STRUCTURE_TYPE,
  UNIT_TYPE
} from '@idc-forms/schemas'
import { Controller, useForm } from 'react-hook-form'
import { useProjectsActiveQuery } from '@/features/projects/hooks/use-projects-query'
import { useTypePropertyByProjectIdQuery } from '@/features/type-properties/hooks/use-type-property-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import { Field, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

const UNIT_TYPE_LABELS: Record<string, string> = {
  [UNIT_TYPE.APT]: 'Apartamento',
  [UNIT_TYPE.V]: 'Villa',
  [UNIT_TYPE.C]: 'Casa'
}

const STRUCTURE_TYPE_LABELS: Record<string, string> = {
  [STRUCTURE_TYPE.T]: 'Torre',
  [STRUCTURE_TYPE.V]: 'Villa'
}

type PropertyFormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreatePropertyInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: CreatePropertyInput) => void
  onCancel: () => void
}

/**
 * Formulario de inmueble (crear/editar) para el side panel. RHF + zodResolver con
 * el MISMO schema (@idc-forms/schemas) que valida el backend vía nestjs-zod: una
 * sola fuente de reglas de validación (ADR 0003).
 */
export function PropertyForm({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: PropertyFormProps) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<CreatePropertyInput>({
    resolver: zodResolver(CreatePropertyInputSchema),
    defaultValues: {
      active: true,
      ...defaultValues
    }
  })

  const selectedProject = watch('project')
  const { data: projects = [] } = useProjectsActiveQuery()
  const { data: typeProperties = [] } = useTypePropertyByProjectIdQuery({
    id: selectedProject || null,
    dependence: Boolean(selectedProject)
  })

  const numberField = (value: number | undefined, onChange: (v: number | undefined) => void) => ({
    value: value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)
    }
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación del inmueble</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="project"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.project)}>
                <FieldLabel htmlFor="property-project">Proyecto</FieldLabel>
                <NativeSelect
                  id="property-project"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    // El subtipo depende del proyecto: al cambiarlo se invalida.
                    setValue('type_property', '')
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona un proyecto
                  </NativeSelectOption>
                  {projects.map((p) => (
                    <NativeSelectOption key={p._id} value={p._id}>
                      {p.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.project]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="type_property"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.type_property)}>
                <FieldLabel htmlFor="property-type">Subtipo</FieldLabel>
                <NativeSelect
                  id="property-type"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                  disabled={!selectedProject}
                >
                  <NativeSelectOption value="" disabled>
                    {selectedProject ? 'Selecciona un subtipo' : 'Primero elige un proyecto'}
                  </NativeSelectOption>
                  {/* Varios subtipos comparten nombre (p.ej. "APARTAMENTO"): el CÓDIGO
                      es el identificador único, el nombre solo acompaña. */}
                  {typeProperties.map((t) => (
                    <NativeSelectOption key={t._id} value={t._id}>
                      {t.name && t.name !== t.code ? `${t.code} — ${t.name}` : t.code}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.type_property]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="property-name">Nombre</FieldLabel>
                <Input id="property-name" value={field.value ?? ''} onChange={field.onChange} />
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.code)}>
                <FieldLabel htmlFor="property-code">Código</FieldLabel>
                <Input id="property-code" value={field.value ?? ''} onChange={field.onChange} />
                <FieldError errors={[errors.code]} />
              </Field>
            )}
          />

          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={control}
              name="unitType"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-unit-type">Tipo de unidad</FieldLabel>
                  <NativeSelect
                    id="property-unit-type"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      field.onChange(e.target.value || undefined)
                    }}
                  >
                    <NativeSelectOption value="">Sin especificar</NativeSelectOption>
                    {Object.values(UNIT_TYPE).map((value) => (
                      <NativeSelectOption key={value} value={value}>
                        {UNIT_TYPE_LABELS[value]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
              )}
            />

            <Controller
              control={control}
              name="structureType"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-structure-type">Estructura</FieldLabel>
                  <NativeSelect
                    id="property-structure-type"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      field.onChange(e.target.value || undefined)
                    }}
                  >
                    <NativeSelectOption value="">Sin especificar</NativeSelectOption>
                    {Object.values(STRUCTURE_TYPE).map((value) => (
                      <NativeSelectOption key={value} value={value}>
                        {STRUCTURE_TYPE_LABELS[value]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={control}
              name="structureNumber"
              render={({ field }) => (
                <Field data-invalid={Boolean(errors.structureNumber)}>
                  <FieldLabel htmlFor="property-structure-number">No. de estructura</FieldLabel>
                  <Input
                    id="property-structure-number"
                    type="number"
                    {...numberField(field.value, field.onChange)}
                  />
                  <FieldError errors={[errors.structureNumber]} />
                </Field>
              )}
            />

            <Controller
              control={control}
              name="identifier"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-identifier">Identificador</FieldLabel>
                  <Input
                    id="property-identifier"
                    type="number"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
          </div>

          <Controller
            control={control}
            name="tipo"
            render={({ field }) => (
              <Field>
                <FieldLabel htmlFor="property-tipo">Tipo</FieldLabel>
                <Input id="property-tipo" value={field.value ?? ''} onChange={field.onChange} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Datos de construcción</FieldLegend>
        <FieldGroup className="gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={control}
              name="constructionM2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-construction">Construcción (m²)</FieldLabel>
                  <Input
                    id="property-construction"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="terrainM2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-terrain-m2">Terreno (m²)</FieldLabel>
                  <Input
                    id="property-terrain-m2"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="terrainV2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-terrain-v2">Terreno (v²)</FieldLabel>
                  <Input
                    id="property-terrain-v2"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="terraceM2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-terrace">Terraza (m²)</FieldLabel>
                  <Input
                    id="property-terrace"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="parkingM2"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-parking">Parqueo (m²)</FieldLabel>
                  <Input
                    id="property-parking"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
          </div>
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Datos adicionales</FieldLegend>
        <FieldGroup className="gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={control}
              name="property"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-finca">Finca</FieldLabel>
                  <Input id="property-finca" value={field.value ?? ''} onChange={field.onChange} />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="folio"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-folio">Folio</FieldLabel>
                  <Input id="property-folio" value={field.value ?? ''} onChange={field.onChange} />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="book"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-book">Libro</FieldLabel>
                  <Input id="property-book" value={field.value ?? ''} onChange={field.onChange} />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="iusiAmount"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-iusi">Monto IUSI</FieldLabel>
                  <Input
                    id="property-iusi"
                    type="number"
                    step="any"
                    {...numberField(field.value, field.onChange)}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="waterMeter"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-water">Contador de agua</FieldLabel>
                  <Input id="property-water" value={field.value ?? ''} onChange={field.onChange} />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="electricityMeter"
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="property-electricity">Contador de luz</FieldLabel>
                  <Input
                    id="property-electricity"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                </Field>
              )}
            />
          </div>

          <Controller
            control={control}
            name="telephone"
            render={({ field }) => (
              <Field>
                <FieldLabel htmlFor="property-telephone">Teléfono</FieldLabel>
                <Input
                  id="property-telephone"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="property-active">Activo</FieldLabel>
                <Switch
                  id="property-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
